import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, LogOut, Download, Clock, Bookmark, ChevronRight, 
  User, Mail, Calendar, Award, TrendingUp, Library, Settings,
  CheckCircle, Lock, Sparkles, Share2, MessageCircle
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MyLibrary = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('biblioteca');

  useEffect(() => {
    const token = localStorage.getItem('reader_token');
    const savedUser = localStorage.getItem('reader_user');
    
    if (!token || !savedUser) {
      navigate('/reader/login');
      return;
    }

    setUser(JSON.parse(savedUser));
    loadProgress(token);
  }, [navigate]);

  const loadProgress = async (token) => {
    try {
      const response = await axios.get(`${API}/reader/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
    } catch (error) {
      console.error('Error loading progress:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('reader_token');
    localStorage.removeItem('reader_user');
    localStorage.removeItem('reader_bonus_pages');
    toast.success('Sesión cerrada');
    navigate('/reader/login');
  };

  const handleDownloadPDF = async () => {
    if (!progress?.hasPaid) {
      toast.error('Necesitás comprar el libro para descargarlo');
      return;
    }

    const token = localStorage.getItem('reader_token');
    
    // Para iOS/Safari, abrir directamente la URL del PDF en una nueva pestaña
    // El navegador mostrará el PDF y el usuario puede guardarlo
    if (/iPhone|iPad|iPod|Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent)) {
      toast.info('Abriendo PDF...');
      // Crear URL con token para descarga directa
      const downloadUrl = `${BACKEND_URL}/api/reader/download-book?token=${encodeURIComponent(token)}`;
      window.open(downloadUrl, '_blank');
      toast.success('📥 PDF abierto. Tocá el ícono de compartir (↑) y seleccioná "Guardar en Archivos".');
      return;
    }
    
    // Para otros navegadores, usar fetch + blob
    toast.info('Preparando descarga...');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/reader/download-book`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      
      if (!response.ok) throw new Error('Error al descargar');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Cabala-para-principiantes.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('📥 Descarga iniciada');
      
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (error) {
      toast.error('Error al descargar el PDF');
    }
  };

  const handleShareWhatsApp = () => {
    const shareUrl = `${window.location.origin}/reader/register`;
    const message = `¡Hola! Te recomiendo este libro: "Cábala para principiantes" de Raíz Lecturas. Podés leer las primeras 30 páginas gratis acá: ${shareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('¡Gracias por compartir!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const progressPercent = progress?.lastPage ? Math.round((progress.lastPage / 176) * 100) : 0;
  const pagesRead = progress?.lastPage || 1;
  const estimatedMinutes = Math.max(0, Math.round((176 - pagesRead) * 1.5));
  const timeSpent = Math.round(pagesRead * 1.5);

  // Books data (prepared for multiple books in the future)
  const books = [
    {
      id: 'cabala-principiantes',
      title: 'Cábala para principiantes',
      subtitle: 'Una guía práctica para el día a día',
      author: 'Agustín Mata',
      cover: '/images/book-cover.png',
      totalPages: 176,
      currentPage: pagesRead,
      purchased: progress?.hasPaid || false,
      progress: progressPercent,
      bookmarks: progress?.bookmarks?.length || 0
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-700/50 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <h1 className="font-semibold text-white">{user?.name}</h1>
                <p className="text-xs text-gray-400">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors px-3 py-2 rounded-lg hover:bg-gray-800"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-gray-900/50 border-b border-gray-700/50">
        <div className="max-w-6xl mx-auto px-4">
          <nav className="flex gap-1">
            {[
              { id: 'biblioteca', label: 'Mi Biblioteca', icon: Library },
              { id: 'perfil', label: 'Mi Perfil', icon: User },
              { id: 'estadisticas', label: 'Estadísticas', icon: TrendingUp },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 ${
                  activeTab === tab.id 
                    ? 'text-amber-500 border-amber-500 bg-amber-500/5' 
                    : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800/50'
                }`}
              >
                <tab.icon size={18} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        
        {/* Biblioteca Tab */}
        {activeTab === 'biblioteca' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Mis Libros</h2>
              <span className="text-sm text-gray-400">{books.length} libro{books.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Books Grid */}
            <div className="grid gap-6">
              {books.map(book => (
                <Card key={book.id} className="bg-gray-800/50 border-gray-700/50 overflow-hidden hover:bg-gray-800/70 transition-all">
                  <div className="md:flex">
                    {/* Book Cover */}
                    <div className="md:w-48 p-6 flex items-center justify-center bg-gradient-to-br from-gray-700/30 to-gray-800/30">
                      <img 
                        src={book.cover} 
                        alt={book.title}
                        className="w-32 md:w-full max-w-[150px] rounded-lg shadow-2xl transform hover:scale-105 transition-transform"
                      />
                    </div>

                    {/* Book Info */}
                    <CardContent className="flex-1 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-white mb-1">{book.title}</h3>
                          <p className="text-gray-400 text-sm">{book.subtitle}</p>
                          <p className="text-amber-500 text-sm mt-1">Por {book.author}</p>
                        </div>
                        {book.purchased ? (
                          <span className="flex items-center gap-1 bg-green-500/20 text-green-400 text-xs font-semibold px-3 py-1.5 rounded-full">
                            <CheckCircle size={14} />
                            Completo
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 bg-amber-500/20 text-amber-400 text-xs font-semibold px-3 py-1.5 rounded-full">
                            <Sparkles size={14} />
                            Preview
                          </span>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-5">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-400">Progreso</span>
                          <span className="font-semibold text-amber-500">{book.progress}%</span>
                        </div>
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
                            style={{ width: `${book.progress}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>Página {book.currentPage} de {book.totalPages}</span>
                          <span>{book.bookmarks} marcadores</span>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <div className="grid grid-cols-3 gap-3 mb-5">
                        <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                          <Clock className="text-amber-500 mx-auto mb-1" size={18} />
                          <p className="text-white font-semibold text-sm">{estimatedMinutes} min</p>
                          <p className="text-gray-500 text-xs">Restantes</p>
                        </div>
                        <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                          <BookOpen className="text-amber-500 mx-auto mb-1" size={18} />
                          <p className="text-white font-semibold text-sm">{book.totalPages - book.currentPage}</p>
                          <p className="text-gray-500 text-xs">Páginas</p>
                        </div>
                        <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                          <Bookmark className="text-amber-500 mx-auto mb-1" size={18} />
                          <p className="text-white font-semibold text-sm">{book.bookmarks}</p>
                          <p className="text-gray-500 text-xs">Guardados</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => navigate('/reader')}
                          className="flex-1 py-3 rounded-full font-bold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center gap-2"
                        >
                          <BookOpen size={20} />
                          {book.currentPage > 1 ? 'Continuar leyendo' : 'Empezar a leer'}
                          <ChevronRight size={18} />
                        </button>

                        {book.purchased ? (
                          <button
                            onClick={handleDownloadPDF}
                            className="sm:w-auto px-6 py-3 rounded-full font-semibold bg-gray-700 hover:bg-gray-600 text-white transition-all flex items-center justify-center gap-2"
                          >
                            <Download size={18} />
                            <span className="sm:hidden lg:inline">PDF</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => navigate('/reader')}
                            className="sm:w-auto px-6 py-3 rounded-full font-semibold border border-amber-500/50 text-amber-500 hover:bg-amber-500/10 transition-all flex items-center justify-center gap-2"
                          >
                            <Lock size={18} />
                            <span>Desbloquear</span>
                          </button>
                        )}

                        {/* WhatsApp Share Button */}
                        <button
                          onClick={handleShareWhatsApp}
                          className="sm:w-auto px-6 py-3 rounded-full font-semibold bg-green-600 hover:bg-green-500 text-white transition-all flex items-center justify-center gap-2"
                          data-testid="share-whatsapp-btn"
                        >
                          <Share2 size={18} />
                          <span className="sm:hidden lg:inline">Compartir</span>
                        </button>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}
            </div>

            {/* Share CTA Card */}
            <Card className="bg-gradient-to-r from-green-600/20 to-green-500/10 border-green-500/30">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Share2 className="text-green-400" size={24} />
                  </div>
                  <div className="text-center sm:text-left flex-1">
                    <h3 className="text-lg font-bold text-white mb-1">¿Te gusta el libro?</h3>
                    <p className="text-gray-400 text-sm">
                      Compartilo con un amigo. Las primeras 30 páginas son gratis.
                    </p>
                  </div>
                  <button
                    onClick={handleShareWhatsApp}
                    className="px-6 py-3 rounded-full font-semibold bg-green-600 hover:bg-green-500 text-white transition-all flex items-center gap-2"
                  >
                    <Share2 size={18} />
                    Compartir por WhatsApp
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Coming Soon */}
            <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
              <CardContent className="p-6 text-center">
                <Sparkles className="text-amber-500 mx-auto mb-3" size={32} />
                <h3 className="text-lg font-bold text-white mb-2">Próximamente más libros</h3>
                <p className="text-gray-400 text-sm">
                  Estamos preparando nuevos títulos para tu biblioteca. ¡Mantente atento!
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Perfil Tab */}
        {activeTab === 'perfil' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="text-2xl font-bold text-white">Mi Perfil</h2>
            
            <Card className="bg-gray-800/50 border-gray-700/50">
              <CardContent className="p-6">
                {/* Avatar & Name */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-700/50">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <span className="text-white font-bold text-3xl">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{user?.name}</h3>
                    <p className="text-gray-400">Lector de Raíz Lecturas</p>
                  </div>
                </div>

                {/* Profile Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-700/30 rounded-lg">
                    <Mail className="text-amber-500" size={22} />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                      <p className="text-white">{user?.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-700/30 rounded-lg">
                    <Calendar className="text-amber-500" size={22} />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Miembro desde</p>
                      <p className="text-white">Febrero 2026</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-700/30 rounded-lg">
                    <Award className="text-amber-500" size={22} />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Estado</p>
                      <p className="text-white">
                        {progress?.hasPaid ? (
                          <span className="text-green-400">Lector Premium</span>
                        ) : (
                          <span className="text-amber-400">Lector Gratuito</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-700/30 rounded-lg">
                    <Library className="text-amber-500" size={22} />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Libros</p>
                      <p className="text-white">
                        {progress?.hasPaid ? '1 libro completo' : '1 libro (preview)'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Upgrade CTA for free users */}
            {!progress?.hasPaid && (
              <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30">
                <CardContent className="p-6 text-center">
                  <Lock className="text-amber-500 mx-auto mb-3" size={32} />
                  <h3 className="text-lg font-bold text-white mb-2">Desbloquea el acceso completo</h3>
                  <p className="text-gray-300 text-sm mb-4">
                    Obtené acceso a todas las páginas y descarga el PDF para leer donde quieras.
                  </p>
                  <button
                    onClick={() => window.open(`https://wa.me/5492604801102?text=${encodeURIComponent('Quiero comprar el libro Kábala para principiantes Raíz lectura')}`, '_blank')}
                    className="px-6 py-3 rounded-full font-bold bg-gradient-to-r from-green-600 to-green-500 text-white inline-flex items-center gap-2"
                  >
                    <MessageCircle size={18} /> Comprar por WhatsApp
                  </button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Estadísticas Tab */}
        {activeTab === 'estadisticas' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="text-2xl font-bold text-white">Mis Estadísticas</h2>
            
            {/* Main Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gray-800/50 border-gray-700/50">
                <CardContent className="p-4 text-center">
                  <BookOpen className="text-amber-500 mx-auto mb-2" size={28} />
                  <p className="text-2xl font-bold text-white">{pagesRead}</p>
                  <p className="text-gray-400 text-xs">Páginas leídas</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800/50 border-gray-700/50">
                <CardContent className="p-4 text-center">
                  <Clock className="text-amber-500 mx-auto mb-2" size={28} />
                  <p className="text-2xl font-bold text-white">{timeSpent}</p>
                  <p className="text-gray-400 text-xs">Minutos leyendo</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800/50 border-gray-700/50">
                <CardContent className="p-4 text-center">
                  <Bookmark className="text-amber-500 mx-auto mb-2" size={28} />
                  <p className="text-2xl font-bold text-white">{progress?.bookmarks?.length || 0}</p>
                  <p className="text-gray-400 text-xs">Marcadores</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800/50 border-gray-700/50">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="text-amber-500 mx-auto mb-2" size={28} />
                  <p className="text-2xl font-bold text-white">{progressPercent}%</p>
                  <p className="text-gray-400 text-xs">Completado</p>
                </CardContent>
              </Card>
            </div>

            {/* Progress Detail */}
            <Card className="bg-gray-800/50 border-gray-700/50">
              <CardContent className="p-6">
                <h3 className="font-bold text-white mb-4">Progreso de lectura</h3>
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Cábala para principiantes</span>
                    <span className="text-amber-500 font-semibold">{progressPercent}%</span>
                  </div>
                  <div className="h-4 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500 relative"
                      style={{ width: `${progressPercent}%` }}
                    >
                      {progressPercent > 10 && (
                        <span className="absolute right-2 top-0 text-xs text-white font-bold leading-4">
                          {progressPercent}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-gray-700/30 rounded-lg">
                    <p className="text-gray-500">Última página</p>
                    <p className="text-white font-semibold">Página {pagesRead}</p>
                  </div>
                  <div className="p-3 bg-gray-700/30 rounded-lg">
                    <p className="text-gray-500">Páginas restantes</p>
                    <p className="text-white font-semibold">{176 - pagesRead} páginas</p>
                  </div>
                  <div className="p-3 bg-gray-700/30 rounded-lg">
                    <p className="text-gray-500">Tiempo estimado</p>
                    <p className="text-white font-semibold">{estimatedMinutes} min</p>
                  </div>
                  <div className="p-3 bg-gray-700/30 rounded-lg">
                    <p className="text-gray-500">Tiempo invertido</p>
                    <p className="text-white font-semibold">{timeSpent} min</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Achievement */}
            <Card className="bg-gray-800/50 border-gray-700/50">
              <CardContent className="p-6">
                <h3 className="font-bold text-white mb-4">Logros</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-lg text-center ${pagesRead >= 5 ? 'bg-amber-500/20' : 'bg-gray-700/30 opacity-50'}`}>
                    <span className="text-2xl">{pagesRead >= 5 ? '🌱' : '🔒'}</span>
                    <p className="text-white text-xs mt-2">Nivel 1</p>
                    <p className="text-gray-500 text-xs">5 páginas</p>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${pagesRead >= 15 ? 'bg-amber-500/20' : 'bg-gray-700/30 opacity-50'}`}>
                    <span className="text-2xl">{pagesRead >= 15 ? '⭐' : '🔒'}</span>
                    <p className="text-white text-xs mt-2">Nivel 2</p>
                    <p className="text-gray-500 text-xs">15 páginas</p>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${pagesRead >= 25 ? 'bg-amber-500/20' : 'bg-gray-700/30 opacity-50'}`}>
                    <span className="text-2xl">{pagesRead >= 25 ? '🎉' : '🔒'}</span>
                    <p className="text-white text-xs mt-2">Nivel 3</p>
                    <p className="text-gray-500 text-xs">25 páginas</p>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${pagesRead >= 40 ? 'bg-amber-500/20' : 'bg-gray-700/30 opacity-50'}`}>
                    <span className="text-2xl">{pagesRead >= 40 ? '👑' : '🔒'}</span>
                    <p className="text-white text-xs mt-2">Nivel 4</p>
                    <p className="text-gray-500 text-xs">40 páginas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-700/50 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
            >
              ← Volver al inicio
            </button>
            <p className="text-gray-600 text-xs">
              © 2026 Raíz Lecturas. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MyLibrary;
